import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Loader2, Clock, MapPin, IdCard, HeartPulse, CheckCircle2, AlertTriangle, Play, Square } from "lucide-react";
import api from "@/lib/api";

const uuid = () => (crypto.randomUUID ? crypto.randomUUID() : String(Date.now() + Math.random()));

const LOC_LABEL = { LOCATION_VERIFIED: "Standort bestätigt", LOCATION_MISMATCH: "Standort weicht ab", LOCATION_UNAVAILABLE: "Standort nicht verfügbar", LOCATION_NOT_CONFIGURED: "Kein Standort hinterlegt" };

export default function CareScan() {
  const { token } = useParams();
  const nav = useNavigate();
  const [ctx, setCtx] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const load = async () => {
    try {
      const r = await api.get(`/worker/care/scan-context/${token}`);
      setCtx(r.data);
    } catch (e) {
      const s = e.response?.status;
      if (s === 401 || s === 403) { localStorage.setItem("eg_return", `/care/scan/${token}`); nav("/login", { replace: true }); return; }
      setErr(e.response?.data?.detail || "Tag nicht gefunden");
    }
  };
  useEffect(() => { load(); }, [token]);

  const scan = async () => {
    setBusy(true);
    const geo = await new Promise((res) => {
      if (!navigator.geolocation) return res(null);
      navigator.geolocation.getCurrentPosition(
        (p) => res({ latitude: p.coords.latitude, longitude: p.coords.longitude, accuracy: p.coords.accuracy }),
        () => res(null), { enableHighAccuracy: true, timeout: 8000, maximumAge: 60000 });
    });
    const ep = ctx.tag_type === "CARE_TIME_TAG" ? "/worker/care/time-scan" : "/worker/care/visit-scan";
    try {
      const r = await api.post(ep, { token, source: "QR", client_request_id: uuid(), latitude: geo?.latitude, longitude: geo?.longitude, accuracy: geo?.accuracy });
      setResult(r.data);
    } catch (e) { setErr(e.response?.data?.detail || "Fehler"); }
    finally { setBusy(false); }
  };

  if (err) return <Wrap><div className="text-center text-red-200" data-testid="carescan-error"><AlertTriangle className="mx-auto mb-2 h-8 w-8" />{err}</div></Wrap>;
  if (!ctx) return <Wrap><Loader2 className="h-6 w-6 animate-spin text-cyan" /></Wrap>;

  const isTime = ctx.tag_type === "CARE_TIME_TAG";

  if (result) {
    const ok = ["CARE_CLOCK_IN", "CARE_CLOCK_OUT", "VISIT_STARTED", "VISIT_ENDED"].includes(result.result);
    const RES = { CARE_CLOCK_IN: "Dienst gestartet", CARE_CLOCK_OUT: "Dienst beendet", VISIT_STARTED: "Patientenbesuch gestartet", VISIT_ENDED: "Patientenbesuch beendet",
      ALREADY_RECORDED: "Bereits erfasst", NOT_ASSIGNED: "Kein zugewiesener Besuch", NOT_ELIGIBLE: "Nicht berechtigt", WRONG_TAG_TYPE: "Falscher Tag-Typ",
      TAG_INACTIVE: "Tag inaktiv", TAG_INVALID: "Tag ungültig", PATIENT_INACTIVE: "Patient inaktiv", DOCUMENTATION_INCOMPLETE: "Dokumentation unvollständig" };
    return (
      <Wrap>
        <div className="w-full text-center" data-testid="carescan-result">
          <div className={`mx-auto flex h-16 w-16 items-center justify-center rounded-2xl ${ok ? "bg-emerald-500/20 text-emerald-300" : "bg-amber-500/20 text-amber-300"}`}>
            {ok ? <CheckCircle2 className="h-8 w-8" /> : <AlertTriangle className="h-8 w-8" />}
          </div>
          <h1 className="mt-4 font-heading text-2xl font-extrabold" data-testid="carescan-result-label">{RES[result.result] || result.result}</h1>
          {result.server_time && <div className="mt-1 text-sm text-slate-400">{new Date(result.server_time).toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit", timeZone: "Europe/Berlin" })} Uhr</div>}
          {result.location_name && <div className="mt-2 text-sm text-cyan"><MapPin className="mr-1 inline h-4 w-4" />{result.location_name}</div>}
          {result.location_verified && <div className="mt-2 text-xs text-slate-400">{LOC_LABEL[result.location_verified] || result.location_verified}</div>}
          {result.review_required && <div className="mt-3 rounded-xl bg-amber-500/10 p-3 text-sm text-amber-200">Prüfung erforderlich</div>}
          {result.message && !ok && <div className="mt-3 text-sm text-slate-300">{result.message}</div>}
          <button onClick={() => { setResult(null); load(); }} className="mt-6 rounded-full border border-white/20 px-6 py-2 text-sm font-semibold hover:bg-white/10" data-testid="carescan-again">Zurück</button>
        </div>
      </Wrap>
    );
  }

  return (
    <Wrap>
      <div className="w-full">
        <div className="mb-6 text-center">
          <span className="font-heading text-2xl font-extrabold">Employ<span className="text-cyan">Guard</span></span>
          <div className="mt-0.5 text-[10px] uppercase tracking-[0.2em] text-slate-400">{isTime ? "Pflege Zeiterfassung" : "Patientenbesuch"}</div>
        </div>
        <div className="mb-5 rounded-2xl bg-white/5 p-5 backdrop-blur">
          <div className="flex items-center gap-2 text-cyan">{isTime ? <Clock className="h-4 w-4" /> : <HeartPulse className="h-4 w-4" />}<span className="font-semibold" data-testid="carescan-title">{isTime ? ctx.location_name : ctx.patient?.name || "Patient"}</span></div>
          <div className="mt-3 space-y-1 border-t border-white/10 pt-3 text-sm">
            <Row label="Mitarbeiter" value={ctx.worker?.name} />
            <Row label={<span><IdCard className="mr-1 inline h-3.5 w-3.5" />Worker ID</span>} value={ctx.worker?.worker_id} />
            {!isTime && <Row label="Patient ID" value={ctx.patient?.patient_number} />}
            {!isTime && <Row label="Besuch" value={ctx.visit ? ctx.visit.visit_number : "—"} />}
          </div>
        </div>

        {isTime ? (
          <button onClick={scan} disabled={busy} data-testid="carescan-time-btn" className="flex w-full items-center justify-center gap-2 rounded-2xl bg-emerald-500 py-6 text-xl font-extrabold hover:bg-emerald-600 disabled:opacity-60">
            {busy ? <Loader2 className="h-6 w-6 animate-spin" /> : ctx.on_shift ? <><Square className="h-6 w-6" /> DIENST BEENDEN</> : <><Play className="h-6 w-6" /> DIENST STARTEN</>}
          </button>
        ) : !ctx.assigned ? (
          <div className="rounded-2xl border border-amber-400/40 bg-amber-400/10 p-5 text-center text-amber-200" data-testid="carescan-not-assigned"><AlertTriangle className="mx-auto mb-2 h-6 w-6" />Kein zugewiesener aktiver Besuch für diesen Patienten.</div>
        ) : (
          <button onClick={scan} disabled={busy} data-testid="carescan-visit-btn" className="flex w-full items-center justify-center gap-2 rounded-2xl bg-cyan py-6 text-xl font-extrabold hover:bg-cyan-dark disabled:opacity-60">
            {busy ? <Loader2 className="h-6 w-6 animate-spin" /> : ctx.visit?.next_action === "VISIT_ENDED" ? <><Square className="h-6 w-6" /> BESUCH BEENDEN</> : <><Play className="h-6 w-6" /> BESUCH STARTEN</>}
          </button>
        )}
        <div className="mt-8 text-center text-xs text-slate-500"><MapPin className="mr-1 inline h-3 w-3" /> Der Tag enthält keine Patientendaten. Zuordnung erfolgt sicher im Backend.</div>
      </div>
    </Wrap>
  );
}

const Row = ({ label, value }) => (<div className="flex justify-between"><span className="text-slate-400">{label}</span><span className="font-medium">{value || "—"}</span></div>);
function Wrap({ children }) { return <div className="flex min-h-screen items-center justify-center bg-navy px-5 py-8 text-white"><div className="mx-auto w-full max-w-md">{children}</div></div>; }
